import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { X, Sparkles, BookOpen, FileText, Loader2, Check, Search as SearchIcon, AlertTriangle, Bot, Eraser } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

// Mapping from button jobType → clear-ai kind. The kinds are deliberately
// namespaced ('mux_' / 'ai_') so the backend's clear router can demux without
// guessing source from job_type alone.
const CLEAR_KIND_BY_JOB_TYPE = {
  find_key_moments:      'mux_find_key_moments',
  generate_chapters:     'mux_generate_chapters',
  summarize:             'mux_summarize',
  ai_find_key_moments:   'ai_find_key_moments',
  ai_generate_chapters:  'ai_generate_chapters',
  ai_summarize:          'ai_summarize',
}

// Multi-select panel for running Mux Robots against a specific set of videos.
// Lists every video with indexed captions (those are the eligible candidates
// for Find Key Moments / Generate Chapters), shows per-Robot status, and
// queues jobs against the explicit selection via /admin/robots-backfill.
//
// Server-side enforcement (in routes/insights.js):
//   - shouldRunJob skips completed/pending/processing unless force=true
//   - withinDailyCap stops the batch when the daily cap is hit
//   - explicit tbtv_ids list is intersected against status='indexed' so
//     callers can't queue jobs against un-indexed assets

const PAGE_SIZE = 100   // big page so the picker shows everything in one view

// Status of one detector on one item. Reads the per-source counts +
// per-job-type robots_jobs status emitted by routes/library.js.
function statusFor(item, jobType) {
  if (!item) return { kind: 'none' }
  let count = 0
  let jobStatus = null

  switch (jobType) {
    case 'find_key_moments':
      count = item.key_moment_count_by_source?.mux || 0
      jobStatus = item.fkm_job_status
      break
    case 'generate_chapters':
      count = item.chapter_count_by_source?.mux || 0
      jobStatus = item.chapters_job_status
      break
    case 'summarize':
      count = item.summary?.by_source?.mux ? 1 : 0
      jobStatus = item.summarize_job_status
      break
    case 'ai_find_key_moments':
      count = (item.key_moment_count_by_source?.tbtv_ai_keyword || 0)
            + (item.key_moment_count_by_source?.tbtv_ai_llm || 0)
      jobStatus = item.ai_fkm_job_status
      break
    case 'ai_generate_chapters':
      count = item.chapter_count_by_source?.tbtv_ai || 0
      jobStatus = item.ai_chapters_job_status
      break
    case 'ai_summarize':
      count = item.summary?.by_source?.tbtv_ai ? 1 : 0
      jobStatus = item.ai_summarize_job_status
      break
  }

  if (jobStatus === 'pending' || jobStatus === 'processing') return { kind: 'in_flight', count }
  if (count > 0) return { kind: 'completed', count }
  if (jobStatus === 'errored') return { kind: 'errored', count }
  return { kind: 'none', count }
}

// For a multi-selection, aggregate across the selected items.
function aggregateStatus(items, selectedIds, jobType) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds)
  let completed = 0, inFlight = 0, errored = 0, none = 0
  let totalCount = 0
  for (const it of items) {
    if (!ids.has(it.tbtv_id)) continue
    const s = statusFor(it, jobType)
    totalCount += s.count
    if (s.kind === 'completed') completed++
    else if (s.kind === 'in_flight') inFlight++
    else if (s.kind === 'errored') errored++
    else none++
  }
  return { completed, inFlight, errored, none, total: ids.size, totalCount }
}

const PILL = {
  none:      { label: '—',         color: '#666',    bg: 'transparent' },
  completed: { label: 'done',      color: '#22c55e', bg: '#062c1a'    },
  in_flight: { label: 'in flight', color: '#d4a72c', bg: '#2a1f04'    },
  errored:   { label: 'errored',   color: '#fca5a5', bg: '#2a1414'    },
}

export default function RobotsApplyModal({ onClose, defaultJobType = 'find_key_moments', preselectTbtvIds = null }) {
  const api = useHighlightsApi()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [q, setQ] = useState('')
  // If caller passed a single video (Caption Library row → Run AI), pre-select it.
  const [selected, setSelected] = useState(() => new Set(preselectTbtvIds || []))
  const [busy, setBusy] = useState(null)        // 'find_key_moments' | 'generate_chapters' | null
  const [result, setResult] = useState(null)
  // Clear AI confirm-dialog target: { jobType, kind, label, totalCount, videoCount }
  const [clearTarget, setClearTarget] = useState(null)
  const [clearBusy, setClearBusy] = useState(false)
  const [activeJobType, setActiveJobType] = useState(defaultJobType)

  // Refs for auto-scrolling the table to a preselected row. When the modal
  // opens from a Caption Library "Run AI" action, the targeted video may be
  // halfway down the indexed list — scroll it into view so the editor sees
  // exactly which row their action will hit.
  const tableScrollRef = useRef(null)
  const selectedRowRef = useRef(null)
  const initialScrollDone = useRef(false)

  const load = useCallback(async (search = q) => {
    setLoading(true)
    setErr(null)
    try {
      const r = await api.library({ status: 'indexed', q: search, limit: PAGE_SIZE })
      setItems(r.items || [])
      setTotal(r.total || 0)
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [api, q])

  useEffect(() => { load() }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Auto-scroll the table to the first preselected row, once the items have
  // loaded. Run-once guard prevents fighting the user if they scroll later.
  useEffect(() => {
    if (initialScrollDone.current) return
    if (items.length === 0) return
    if (selected.size === 0) return
    // Wait one tick so the row's ref is attached.
    requestAnimationFrame(() => {
      if (selectedRowRef.current?.scrollIntoView) {
        selectedRowRef.current.scrollIntoView({ behavior: 'auto', block: 'center' })
        initialScrollDone.current = true
      }
    })
  }, [items, selected])

  // Only Mux-backed indexed rows are eligible (FKM/Chapters are Mux Robots).
  const eligible = useMemo(
    () => items.filter(it => it.backend === 'mux'),
    [items]
  )

  // Pricing for batch cost preview. Falls back gracefully if the call fails.
  const [pricing, setPricing] = useState(null)
  useEffect(() => {
    api.adminPricing().then(setPricing).catch(() => {})
  }, [])

  // Durations of currently-selected videos in seconds — drives cost preview.
  const selectedDurations = useMemo(() => {
    const ids = new Set(selected)
    return eligible.filter(it => ids.has(it.tbtv_id)).map(it => it.duration || 0)
  }, [eligible, selected])

  // Per-detector cost across the current selection. Matches src/pricing.js.
  function estimateSelectionCost(detectorKey) {
    if (!pricing) return null
    const d = pricing.detectors?.[detectorKey]
    if (!d) return null
    if (d.pricing_model === 'free') return 0
    if (d.pricing_model === 'mux') {
      return selectedDurations.reduce((sum, sec) => sum + (sec / 60) * d.per_min + d.per_job, 0)
    }
    if (d.pricing_model === 'llm') {
      const mp = pricing.llm_models?.[pricing.active_llm_model]
      if (!mp) return null
      return selectedDurations.reduce((sum, sec) => {
        const min = sec / 60
        return sum
          + (min * d.token_in_per_min / 1e6) * mp.input_per_million
          + (d.token_out_per_call / 1e6) * mp.output_per_million
      }, 0)
    }
    return null
  }
  function fmtUsd(n) {
    if (n == null) return '—'
    if (n === 0) return 'Free'
    if (n < 0.01) return `$${n.toFixed(4)}`
    return `$${n.toFixed(2)}`
  }

  function toggle(tbtv_id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(tbtv_id)) next.delete(tbtv_id); else next.add(tbtv_id)
      return next
    })
  }

  // "Select all incomplete" — picks rows whose chosen jobType is neither
  // already done NOR currently in flight. Cap at 50 per click to stay well
  // below the worker's sequential-call CPU budget. To re-run a completed
  // detector, Clear AI first then re-apply (no Force checkbox needed).
  function selectIncomplete() {
    const ids = eligible
      .filter(it => {
        const s = statusFor(it, activeJobType)
        if (s.kind === 'in_flight') return false
        if (s.kind === 'completed') return false
        return true // 'none' or 'errored' → pick up
      })
      .slice(0, 50)
      .map(it => it.tbtv_id)
    setSelected(new Set(ids))
  }

  async function apply(jobType) {
    if (selected.size === 0) return
    setBusy(jobType)
    setResult(null)
    try {
      const r = await api.robotsApply({
        jobType,
        tbtvIds: Array.from(selected),
      })
      setResult({ jobType, ...r })
      // Refresh the list so counts reflect newly queued/completed jobs.
      // Per-Robot status here only changes when the webhook later inserts
      // moments/chapters; queued state is in robots_jobs (not exposed by
      // library). User can re-open the modal in a few mins to see updates.
    } catch (e) {
      setResult({ jobType, error: e.message })
    } finally {
      setBusy(null)
    }
  }

  // Open the Clear AI confirm dialog for a specific detector. Reads the
  // aggregateStatus the same way the badge does, so the "N rows will be
  // deleted" line in the dialog matches what the user already sees on the
  // button.
  function openClear(jobType, label) {
    if (selected.size === 0) return
    const agg = aggregateStatus(items, selected, jobType)
    if (agg.totalCount === 0) return  // nothing to clear (link should be disabled, but defensive)
    setClearTarget({
      jobType,
      kind: CLEAR_KIND_BY_JOB_TYPE[jobType],
      label,
      totalCount: agg.totalCount,
      videoCount: agg.completed,
    })
  }

  async function confirmClear() {
    if (!clearTarget) return
    setClearBusy(true)
    setResult(null)
    try {
      const r = await api.clearAI({
        tbtvIds: Array.from(selected),
        kinds: [clearTarget.kind],
        resetJobs: true,
      })
      const totalDeleted = Object.values(r.deleted || {}).reduce((a, b) => a + b, 0)
      const jobsReset = Object.values(r.jobs_reset || {}).reduce((a, b) => a + b, 0)
      setResult({
        jobType: clearTarget.jobType,
        cleared: true,
        label: clearTarget.label,
        deleted_rows: totalDeleted,
        jobs_reset: jobsReset,
      })
      setClearTarget(null)
      // Reload so the badges reflect the new (zeroed) counts.
      await load(q)
    } catch (e) {
      setResult({ jobType: clearTarget.jobType, error: e.message })
      setClearTarget(null)
    } finally {
      setClearBusy(false)
    }
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div style={{
        background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(960px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Apply or clear AI on videos</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              Select videos, then queue a detector with its colored button or clear existing output with the 🧹 icon. Daily cap + per-Robot toggle still apply. Captions are never affected by Clear.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={(e) => { e.preventDefault(); load(q) }} style={{ display: 'flex', flex: 1, minWidth: 240, gap: 6, alignItems: 'center' }}>
            <SearchIcon size={14} style={{ color: '#888' }} />
            <input
              type="text"
              placeholder="Filter by title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ flex: 1, padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
            />
          </form>
          <select
            value={activeJobType}
            onChange={(e) => setActiveJobType(e.target.value)}
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 12 }}>
            <option value="find_key_moments">Status column: Find Key Moments</option>
            <option value="generate_chapters">Status column: Generate Chapters</option>
          </select>
          <button onClick={selectIncomplete}
            style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12 }}>
            Select incomplete (up to 50)
          </button>
          <button onClick={() => setSelected(new Set())}
            style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12 }}
            title="Reset selection checkboxes (does not delete anything)">
            Clear selection
          </button>
        </div>

        {/* Action bar — two rows: header, then Mux + TBTV-AI buttons. */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
            <span style={{ fontSize: 11, color: '#777' }}>· Click 🧹 next to a detector to clear its output (captions are preserved)</span>
            <div style={{ flex: 1 }} />
          </div>

          {/* Mux row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <span style={{ fontSize: 11, color: '#888', minWidth: 60, fontWeight: 600 }}>Mux</span>
            <ApplyButton
              icon={<Sparkles size={12} />}
              label="Find Key Moments"
              cost={fmtUsd(estimateSelectionCost('mux_find_key_moments'))}
              status={aggregateStatus(items, selected, 'find_key_moments')}
              onClick={() => apply('find_key_moments')}
              onClear={() => openClear('find_key_moments', 'Mux Find Key Moments')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'find_key_moments'}
            />
            <ApplyButton
              icon={<BookOpen size={12} />}
              label="Generate Chapters"
              cost={fmtUsd(estimateSelectionCost('mux_generate_chapters'))}
              status={aggregateStatus(items, selected, 'generate_chapters')}
              onClick={() => apply('generate_chapters')}
              onClear={() => openClear('generate_chapters', 'Mux Generate Chapters')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'generate_chapters'}
            />
            <ApplyButton
              icon={<FileText size={12} />}
              label="Summarize"
              cost={fmtUsd(estimateSelectionCost('mux_summarize'))}
              status={aggregateStatus(items, selected, 'summarize')}
              onClick={() => apply('summarize')}
              onClear={() => openClear('summarize', 'Mux Summary')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'summarize'}
            />
          </div>

          {/* TBTV-AI row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <span style={{ fontSize: 11, color: '#a78bfa', minWidth: 60, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Bot size={12} /> TBTV-AI
            </span>
            <ApplyButton
              icon={<Sparkles size={12} />}
              label="Find Key Moments"
              cost={fmtUsd(estimateSelectionCost('ai_find_key_moments_llm'))}
              sublabel="keyword free + LLM"
              status={aggregateStatus(items, selected, 'ai_find_key_moments')}
              onClick={() => apply('ai_find_key_moments')}
              onClear={() => openClear('ai_find_key_moments', 'TBTV-AI Find Key Moments')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'ai_find_key_moments'}
              tone="ai"
            />
            <ApplyButton
              icon={<BookOpen size={12} />}
              label="Generate Chapters"
              cost={fmtUsd(estimateSelectionCost('ai_generate_chapters'))}
              status={aggregateStatus(items, selected, 'ai_generate_chapters')}
              onClick={() => apply('ai_generate_chapters')}
              onClear={() => openClear('ai_generate_chapters', 'TBTV-AI Generate Chapters')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'ai_generate_chapters'}
              tone="ai"
            />
            <ApplyButton
              icon={<FileText size={12} />}
              label="Summarize"
              cost={fmtUsd(estimateSelectionCost('ai_summarize'))}
              status={aggregateStatus(items, selected, 'ai_summarize')}
              onClick={() => apply('ai_summarize')}
              onClear={() => openClear('ai_summarize', 'TBTV-AI Summary')}
              disabled={selected.size === 0 || busy}
              busy={busy === 'ai_summarize'}
              tone="ai"
            />
          </div>
        </div>

        {/* Result panel */}
        {result && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #1f1f1f', fontSize: 12, background: result.error ? '#2a1414' : (result.cleared ? '#1f1414' : '#0c1f14') }}>
            {result.error ? (
              <span style={{ color: '#fca5a5' }}>{result.jobType}: {result.error}</span>
            ) : result.cleared ? (
              <>
                <strong style={{ color: '#fca5a5' }}>Cleared {result.label}:</strong>{' '}
                deleted <strong>{result.deleted_rows}</strong> row{result.deleted_rows === 1 ? '' : 's'}
                {result.jobs_reset > 0 && <>, reset <strong>{result.jobs_reset}</strong> job record{result.jobs_reset === 1 ? '' : 's'}</>}
                . Re-apply via the same row.
              </>
            ) : (
              <>
                <strong style={{ color: '#86efac' }}>{result.jobType}:</strong>{' '}
                queued <strong>{result.queued}</strong>
                {result.skipped > 0 && <>, skipped <strong>{result.skipped}</strong> (already done)</>}
                {result.errors > 0 && <>, errored <strong style={{ color: '#fca5a5' }}>{result.errors}</strong></>}
                {result.cap_hit && (
                  <span style={{ color: '#f59e0b', marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <AlertTriangle size={11} /> daily cap reached
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Clear AI confirm dialog */}
        {clearTarget && (
          <ClearConfirmDialog
            target={clearTarget}
            busy={clearBusy}
            onCancel={() => !clearBusy && setClearTarget(null)}
            onConfirm={confirmClear}
          />
        )}

        {/* Selected-videos banner — surfaces what the Apply / Clear buttons
            will affect. Especially important when the modal opens with a
            preselected video (Caption Library "Run AI" row action) since
            that video may otherwise be scrolled out of view in the table. */}
        {selected.size > 0 && (() => {
          const selectedItems = items.filter(it => selected.has(it.tbtv_id))
          const visible = selectedItems.slice(0, 3)
          const more = selectedItems.length - visible.length
          return (
            <div style={{
              padding: '8px 16px',
              background: '#0d1626',
              borderBottom: '1px solid #1f2a40',
              fontSize: 12,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}>
              <span style={{ color: '#7dd3fc', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                title="Whichever Apply / Clear button you click below will run against this video. Color is just informational — it doesn't indicate a queued action.">
                Selected {selected.size === 1 ? 'video' : `videos (${selected.size})`}:
              </span>
              <span style={{ color: '#cbd5e1', flex: 1, minWidth: 0 }}>
                {visible.map((it, idx) => (
                  <span key={it.tbtv_id}>
                    <span style={{ fontWeight: 500 }}>{it.title || '(untitled)'}</span>
                    {idx < visible.length - 1 && <span style={{ color: '#666' }}> · </span>}
                  </span>
                ))}
                {more > 0 && <span style={{ color: '#888' }}> · +{more} more</span>}
              </span>
              {selectedItems.length === 1 && selectedItems[0]?.tbtv_id && (
                <span style={{ color: '#666', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {selectedItems[0].tbtv_id}
                </span>
              )}
            </div>
          )
        })()}

        {/* Table */}
        <div ref={tableScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
          {err && <div style={{ padding: 16, color: 'crimson' }}>{err}</div>}
          {loading && items.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
              <Loader2 size={14} className="spin" /> Loading indexed videos…
            </div>
          )}
          {!loading && eligible.length === 0 && !err && (
            <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
              No indexed Mux-backed videos. Activate content with captions first.
            </div>
          )}

          {eligible.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#161616', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '8px 12px', width: 32 }}>
                    <input
                      type="checkbox"
                      checked={eligible.length > 0 && eligible.every(it => selected.has(it.tbtv_id))}
                      onChange={() => {
                        const allSelected = eligible.every(it => selected.has(it.tbtv_id))
                        setSelected(allSelected ? new Set() : new Set(eligible.map(it => it.tbtv_id)))
                      }}
                    />
                  </th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', width: 110 }}>
                    {activeJobType === 'find_key_moments' ? 'Key Moments' : 'Chapters'}
                  </th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', width: 100 }}>Other</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // First selected tbtv_id (in render order) gets the scroll
                  // ref so the auto-scroll effect can find it.
                  const firstSelectedId = eligible.find(it => selected.has(it.tbtv_id))?.tbtv_id
                  return eligible.map(it => {
                  const isSelected = selected.has(it.tbtv_id)
                  const primary = statusFor(it, activeJobType)
                  const other = statusFor(it, activeJobType === 'find_key_moments' ? 'generate_chapters' : 'find_key_moments')
                  return (
                    <tr
                      key={it.tbtv_id}
                      ref={it.tbtv_id === firstSelectedId ? selectedRowRef : null}
                      style={{
                        borderBottom: '1px solid #1a1a1a',
                        background: isSelected ? 'rgba(125,211,252,0.06)' : 'transparent',
                      }}>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(it.tbtv_id)}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }}>{it.title || '(untitled)'}</div>
                        <div style={{ fontSize: 11, color: '#777', fontFamily: 'monospace' }}>{it.tbtv_id}</div>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <StatusPill kind={primary.kind} count={primary.count} />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <StatusPill kind={other.kind} count={other.count} small />
                      </td>
                    </tr>
                  )
                })
                })()}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid #1f1f1f', fontSize: 11, color: '#888' }}>
          {eligible.length} / {total} indexed videos shown
          {items.length > eligible.length && ` · ${items.length - eligible.length} non-Mux hidden`}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    </div>
  )
}

function StatusPill({ kind, count, small }) {
  const meta = PILL[kind] || PILL.none
  return (
    <span
      title={kind === 'in_flight' ? 'Mux is processing this Robot job' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: small ? '0 6px' : '2px 8px',
        borderRadius: 10,
        fontSize: small ? 10 : 11,
        background: meta.bg,
        color: meta.color,
      }}>
      {kind === 'completed' && <Check size={small ? 9 : 10} />}
      {kind === 'completed' && count > 0
        ? `${count} ${count === 1 ? 'result' : 'results'}`
        : meta.label}
    </span>
  )
}

// Renders a small status pill embedded in the action button so editors see
// at a glance which detectors have already been run for the current
// selection. Handles both single-video (1 selected) and batch cases.
function StatusInButton({ status }) {
  if (!status || status.total === 0) return null
  const { completed, inFlight, errored, none, total, totalCount } = status
  // Badges sit inside colored buttons (blue #3b82f6 or purple #7c3aed) so they
  // need high contrast against both backgrounds. White/near-white text on a
  // semi-opaque dark bg reads clearly on either.
  // Single video — concise badge.
  if (total === 1) {
    if (inFlight)  return <Badge color="#fef3c7" bg="rgba(0,0,0,0.45)" label="⏳ in flight" />
    if (completed) return <Badge color="#bbf7d0" bg="rgba(0,0,0,0.45)" label={totalCount > 1 ? `✓ ${totalCount}` : '✓ done'} />
    if (errored)   return <Badge color="#fecaca" bg="rgba(0,0,0,0.45)" label="✗ errored" />
    return <Badge color="#f5f5f5" bg="rgba(0,0,0,0.45)" label="not run" />
  }
  // Multi-select — aggregate counts.
  const parts = []
  if (completed) parts.push(`✓ ${completed}`)
  if (inFlight)  parts.push(`⏳ ${inFlight}`)
  if (errored)   parts.push(`✗ ${errored}`)
  if (none)      parts.push(`▫ ${none}`)
  return (
    <Badge
      color="#f5f5f5"
      bg="rgba(0,0,0,0.45)"
      label={`${parts.join(' · ')} of ${total}`}
    />
  )
}

function Badge({ color, bg, label }) {
  return (
    <span style={{
      fontSize: 10, padding: '1px 6px', borderRadius: 10,
      background: bg, color, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  )
}

function ApplyButton({ icon, label, cost, sublabel, status, onClick, onClear, disabled, busy, tone = 'mux' }) {
  const enabled = !disabled
  const bg = enabled ? (tone === 'ai' ? '#7c3aed' : '#3b82f6') : '#222'
  // Clear is only meaningful when ≥1 of the selected videos has completed
  // rows for this detector — anything else is a no-op delete.
  const clearable = enabled && onClear && (status?.totalCount > 0)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'stretch', gap: 0 }}>
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: '6px 10px',
          background: bg,
          border: 'none',
          borderRadius: onClear ? '4px 0 0 4px' : 4,
          color: 'white',
          cursor: enabled ? 'pointer' : 'default',
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}>
        {busy ? <Loader2 size={12} className="spin" /> : icon}
        <span>{label}</span>
        {cost && <span style={{ opacity: 0.85, fontSize: 11 }}>· {cost}</span>}
        {sublabel && <span style={{ opacity: 0.7, fontSize: 10 }}>({sublabel})</span>}
        {status && <StatusInButton status={status} />}
      </button>
      {onClear && (
        <button
          onClick={clearable ? onClear : undefined}
          disabled={!clearable}
          title={clearable
            ? `Clear ${status.totalCount} row${status.totalCount === 1 ? '' : 's'} of ${label.toLowerCase()} (captions preserved)`
            : 'Nothing to clear for this detector on the selected videos'}
          style={{
            padding: '6px 8px',
            background: clearable ? '#3a1414' : '#1a1a1a',
            border: 'none',
            borderLeft: '1px solid rgba(0,0,0,0.3)',
            borderRadius: '0 4px 4px 0',
            color: clearable ? '#fca5a5' : '#444',
            cursor: clearable ? 'pointer' : 'default',
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
          }}>
          <Eraser size={12} />
        </button>
      )}
    </span>
  )
}

// Confirm dialog for Clear AI on a specific detector. Surfaces what's about
// to be deleted in concrete numbers — the editor reads exactly what they're
// agreeing to. Backend deletes scoped to (tbtv_ids × kind); captions/cues/
// transcripts are never touched.
function ClearConfirmDialog({ target, busy, onCancel, onConfirm }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
      }}>
      <div style={{
        background: '#1a0e0e', border: '1px solid #5b1f1f', borderRadius: 8,
        width: 'min(480px, 92vw)', padding: 18, fontSize: 13,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Eraser size={16} style={{ color: '#fca5a5' }} />
          <strong style={{ fontSize: 15 }}>Clear {target.label}?</strong>
        </div>
        <div style={{ marginBottom: 14, color: '#ddd', lineHeight: 1.5 }}>
          This will delete <strong style={{ color: '#fca5a5' }}>{target.totalCount}</strong> row{target.totalCount === 1 ? '' : 's'} of <strong>{target.label}</strong> output across <strong>{target.videoCount}</strong> selected video{target.videoCount === 1 ? '' : 's'}.
          <div style={{ marginTop: 8, padding: '8px 10px', background: '#0c1f14', border: '1px solid #1a3a26', borderRadius: 4, fontSize: 12, color: '#9eddb6' }}>
            <Check size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Captions, transcript cues, and search indexes are preserved.
          </div>
        </div>
        <div style={{ marginBottom: 14, fontSize: 11, color: '#888' }}>
          Job records are reset too, so re-applying via the same row will auto-fire without a force flag.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '7px 14px', background: 'transparent', border: '1px solid #333',
              borderRadius: 4, color: 'inherit', cursor: busy ? 'default' : 'pointer', fontSize: 12,
            }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: '7px 14px', background: busy ? '#3a1414' : '#7f1d1d', border: 'none',
              borderRadius: 4, color: 'white', cursor: busy ? 'default' : 'pointer', fontSize: 12,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            {busy && <Loader2 size={12} className="spin" />}
            {busy ? 'Clearing…' : `Clear ${target.totalCount} row${target.totalCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
